import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup.js";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/hooks";
import { nameSchema } from "@shared/utils/yupSchema";
import { updateCurrentUser, uploadAvatar } from "@services/api";

const MAX_AVATAR_BYTES = 5 * 1024 * 1024; // 5 MB
const ACCEPTED_AVATAR_TYPES = ["image/png", "image/jpeg"];
import { addUserInfo } from "@store/slices/authSlice";
import { useNotification } from "@shared/contexts/NotificationContext";

interface NameForm {
  fullName: string;
  username: string;
}

// initials for the placeholder avatar when avatar_url is null.
const initials = (full?: string | null, username?: string | null) => {
  if (full) {
    return full
      .split(/\s+/)
      .map((s) => s[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase();
  }
  return (username ?? "?").slice(0, 2).toUpperCase();
};

export const ProfilePanel = () => {
  const dispatch = useAppDispatch();
  const { userInfo } = useAppSelector((state) => state.auth);
  const { userId } = useAppSelector((state) => state.user);
  const { showSuccess, showError } = useNotification();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<NameForm>({ resolver: yupResolver(nameSchema) });

  const onSubmit = async (data: NameForm) => {
    if (!userId) return;
    const payload: { full_name?: string; username?: string } = {};
    if (data.fullName) payload.full_name = data.fullName;
    if (data.username) payload.username = data.username;
    if (!payload.full_name && !payload.username) return;

    const r = await updateCurrentUser(payload);
    if (!r.success) {
      showError(r.error.message || "Could not update profile");
      return;
    }
    showSuccess("Profile updated");
    dispatch(addUserInfo(userId));
    reset({ fullName: "", username: "" });
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const handleAvatarChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the same file be re-picked after an error
    if (!file || !userId) return;
    if (!ACCEPTED_AVATAR_TYPES.includes(file.type)) {
      showError("Avatar must be a PNG or JPEG image.");
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      showError("Avatar must be 5 MB or smaller.");
      return;
    }
    setUploadingAvatar(true);
    const r = await uploadAvatar(file);
    setUploadingAvatar(false);
    if (!r.success) {
      showError(r.error.message || "Could not upload avatar.");
      return;
    }
    showSuccess("Avatar updated");
    dispatch(addUserInfo(userId));
  };

  return (
    <Paper variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Profile
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        How you appear to collaborators.
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg"
          hidden
          onChange={handleAvatarChange}
        />
        <Box sx={{ position: "relative", width: 64, height: 64 }}>
          <Avatar
            src={userInfo?.avatar_url ?? undefined}
            sx={{ width: 64, height: 64 }}
          >
            {initials(userInfo?.full_name, userInfo?.username)}
          </Avatar>
          {uploadingAvatar && (
            <CircularProgress
              size={64}
              sx={{ position: "absolute", top: 0, left: 0 }}
            />
          )}
        </Box>
        <Box>
          <Typography variant="subtitle1">
            {userInfo?.full_name ?? userInfo?.username ?? "—"}
          </Typography>
          {userInfo?.full_name && userInfo?.username && (
            <Typography variant="body2" color="text.secondary">
              @{userInfo.username}
            </Typography>
          )}
          <Button
            variant="outlined"
            color="inherit"
            size="small"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingAvatar}
            sx={{ mt: 0.5, textTransform: "none" }}
          >
            {uploadingAvatar ? "Uploading…" : "Change photo"}
          </Button>
        </Box>
      </Box>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          sx={{ mb: 2 }}
        >
          <TextField
            {...register("fullName")}
            label="Full name"
            placeholder={userInfo?.full_name ?? "—"}
            size="small"
            fullWidth
            error={!!errors.fullName}
            helperText={errors.fullName?.message}
          />
          <TextField
            {...register("username")}
            label="Username"
            placeholder={userInfo?.username ?? "—"}
            size="small"
            fullWidth
            error={!!errors.username}
            helperText={errors.username?.message}
          />
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            type="submit"
            variant="outlined"
            color="inherit"
            disabled={isSubmitting || !isDirty}
          >
            Save changes
          </Button>
          <Button
            type="button"
            variant="outlined"
            color="cancel"
            disabled={isSubmitting || !isDirty}
            onClick={() => reset({ fullName: "", username: "" })}
          >
            Cancel
          </Button>
        </Stack>
      </form>
    </Paper>
  );
};

export default ProfilePanel;
